import React, {
    createContext,
    useCallback,
    useContext,
    useState,
    ReactNode,
} from "react";

export enum PopupType {
    SEARCH = "SEARCH",
    ONBOARDING = "ONBOARDING",
    API_PROMO = "API_PROMO",
    CHANGELOG = "CHANGELOG",
    DATA_DRIVEN = "DATA_DRIVEN",
    COMMENT_DELETE = "COMMENT_DELETE",
    FRONT_TRAILER = "FRONT_TRAILER",
    FRONT_PRESENTER_REEL = "FRONT_PRESENTER_REEL",
}

/**
 * Default priorities for the hardcoded / experience popups. Higher wins.
 * Data-driven popups pass their own server-provided priority to openPopup,
 * so the DATA_DRIVEN value here is only a fallback.
 *
 * - ONBOARDING sits above passive popups so first-run onboarding wins.
 * - CHANGELOG is user-invoked (info icon) so it should beat marketing popups.
 * - SEARCH is intentionally very low so any incoming popup can replace it.
 * - COMMENT_DELETE is a destructive confirmation and cannot be interrupted.
 */
export const PopupPriority: Record<PopupType, number> = {
    [PopupType.ONBOARDING]: 9999,
    [PopupType.CHANGELOG]: 9000,
    [PopupType.API_PROMO]: 100,
    [PopupType.DATA_DRIVEN]: 0,
    [PopupType.SEARCH]: -1000,
    // The homepage trailer is explicitly requested, like the changelog.
    [PopupType.FRONT_TRAILER]: 9000,
    // Presenter reels are also explicitly requested by the visitor.
    [PopupType.FRONT_PRESENTER_REEL]: 9000,
    // A destructive confirmation already in progress must not be replaced.
    [PopupType.COMMENT_DELETE]: Number.MAX_SAFE_INTEGER,
};

interface PopupState {
    type: PopupType | null;
    data: any;
    priority: number;
}

interface PopupContextTypes {
    activePopup: PopupType | null;
    activePriority: number;
    popupData: any;
    openPopup: (type: PopupType, data?: any, priority?: number) => void;
    closePopup: () => void;
    closePopupIfType: (expectedType: PopupType) => void;
    isPopupOpen: (type: PopupType) => boolean;
}

export const PopupContext = createContext<PopupContextTypes>({} as PopupContextTypes);

const EMPTY_STATE: PopupState = { type: null, data: null, priority: -Infinity };

// Two open requests are "the same popup" when the type matches — and, for
// data-driven popups, when the popupId matches too. Used to avoid re-opening
// (and re-animating) the popup that is already showing.
const isSamePopup = (state: PopupState, type: PopupType, data: any): boolean => {
    if (state.type !== type) return false;
    if (type === PopupType.DATA_DRIVEN) {
        return state.data?.popupId === data?.popupId;
    }
    return true;
};

export const PopupContextProvider = (props: { children: ReactNode }) => {
    const [state, setState] = useState<PopupState>(EMPTY_STATE);

    const openPopup = useCallback((type: PopupType, data: any = null, priority?: number) => {
        const incomingPriority = priority ?? PopupPriority[type] ?? 0;

        setState((prev) => {
            if (prev.type !== null) {
                // Already showing this exact popup — nothing to do.
                if (isSamePopup(prev, type, data)) return prev;

                // A lower- or equal-priority popup cannot replace the current one.
                // (Equal priority keeps the first/incumbent, per the spec.)
                if (incomingPriority <= prev.priority) return prev;
            }

            return { type, data, priority: incomingPriority };
        });
    }, []);

    const closePopup = useCallback(() => setState(EMPTY_STATE), []);

    const closePopupIfType = useCallback((expectedType: PopupType) => {
        setState((prev) => {
            return prev.type === expectedType ? EMPTY_STATE : prev;
        });
    }, []);

    const isPopupOpen = useCallback(
        (type: PopupType) => state.type === type,
        [state.type]
    );

    const value: PopupContextTypes = {
        activePopup: state.type,
        activePriority: state.priority,
        popupData: state.data,
        openPopup,
        closePopup,
        closePopupIfType,
        isPopupOpen,
    };

    return <PopupContext.Provider value={value}>{props.children}</PopupContext.Provider>;
};

export const usePopup = () => {
    const context = useContext(PopupContext);
    if (!context) {
        throw new Error("usePopup must be used within a PopupProvider");
    }
    return context;
};
